/**
 * IncomingCallModal - Shows incoming call notification with accept/reject buttons
 * 
 * PHASE 3: WebRTC Support
 * - Accept triggers WebRTC SDP answer generation
 * - Connects audio when call is accepted
 */

import React, { useContext, useEffect, useState } from 'react';
import { CallContext, CallStatus } from '../CallContext';
import './IncomingCallModal.css';

// Icons
import PhoneIcon from '@mui/icons-material/Phone';
import CallEndIcon from '@mui/icons-material/CallEnd';
import PersonIcon from '@mui/icons-material/Person';

const RING_TIMEOUT = 30; // seconds

function IncomingCallModal() {
    const {
        currentCall,
        showIncomingCall,
        acceptCall,
        rejectCall,
        dismissIncomingCall,
        connectionState,
    } = useContext(CallContext);

    const [countdown, setCountdown] = useState(RING_TIMEOUT);
    const [isProcessing, setIsProcessing] = useState(false);
    const [actionError, setActionError] = useState(null);
    const [acceptStatus, setAcceptStatus] = useState('');

    // Countdown timer
    useEffect(() => {
        if (!showIncomingCall || !currentCall) {
            setCountdown(RING_TIMEOUT);
            setAcceptStatus('');
            return;
        }

        const timer = setInterval(() => {
            setCountdown(prev => {
                if (prev <= 1) {
                    handleReject('timeout');
                    return RING_TIMEOUT;
                }
                return prev - 1;
            });
        }, 1000);

        return () => clearInterval(timer);
    }, [showIncomingCall, currentCall]);

    // Play ringtone effect
    useEffect(() => {
        if (showIncomingCall) {
            document.title = '📞 Incoming Call...';
        } else {
            document.title = 'WhatsApp Business';
        }

        return () => {
            document.title = 'WhatsApp Business';
        };
    }, [showIncomingCall]);

    const handleAccept = async () => {
        if (isProcessing || !currentCall) return;

        setIsProcessing(true);
        setActionError(null);
        setAcceptStatus('Requesting microphone...');

        try {
            // acceptCall now handles:
            // 1. Get microphone access
            // 2. Create WebRTC answer from WhatsApp's SDP offer
            // 3. Send answer to backend -> WhatsApp API
            const result = await acceptCall(currentCall.call_id, {
                agent_id: 'web-agent',
                agent_name: 'Web Agent',
            });

            if (result.success) {
                setAcceptStatus('Connecting...');
            } else {
                setActionError(result.error || 'Failed to accept call');
                setAcceptStatus('');
            }
        } catch (e) {
            setActionError(e.message);
            setAcceptStatus('');
        } finally {
            setIsProcessing(false);
        }
    };

    const handleReject = async (reason = 'agent_rejected') => {
        if (isProcessing || !currentCall) return;

        setIsProcessing(true);
        setActionError(null);

        try {
            const result = await rejectCall(currentCall.call_id, reason);

            if (!result.success) {
                setActionError(result.error || 'Failed to reject call');
            }
        } catch (e) {
            setActionError(e.message);
        } finally {
            setIsProcessing(false);
        }
    };

    if (!showIncomingCall || !currentCall) {
        return null;
    }

    // Only show for ringing inbound calls
    if (currentCall.status !== CallStatus.RINGING ||
        currentCall.direction !== 'inbound') {
        return null;
    }

    // Format phone number for display
    const formatPhone = (phone) => {
        if (!phone) return 'Unknown';
        if (phone.length > 10) {
            return `+${phone.slice(0, 2)} ${phone.slice(2, 7)} ${phone.slice(7)}`;
        }
        return phone;
    };

    return (
        <div className="incoming-call-overlay">
            <div className="incoming-call-modal">
                {/* Caller Info */}
                <div className="caller-info">
                    <div className="caller-avatar pulsing">
                        <PersonIcon style={{ fontSize: 48 }} />
                    </div>
                    <div className="caller-details">
                        <h2 className="caller-name">Incoming Call</h2>
                        <p className="caller-phone">{formatPhone(currentCall.caller_phone)}</p>
                        <p className="call-type">WhatsApp Voice Call</p>
                    </div>
                </div>

                {/* Timer */}
                <div className="call-timer">
                    <span className="timer-text">
                        Auto-reject in {countdown}s
                    </span>
                    <div className="timer-bar">
                        <div
                            className="timer-progress"
                            style={{ width: `${(countdown / RING_TIMEOUT) * 100}%` }}
                        />
                    </div>
                </div>

                {/* Status/Error Message */}
                {actionError && (
                    <div className="call-error">
                        {actionError}
                    </div>
                )}
                {acceptStatus && !actionError && (
                    <div className="call-status">
                        {acceptStatus}
                    </div>
                )}

                {/* Action Buttons */}
                <div className="call-actions">
                    <button
                        className="call-button reject"
                        onClick={() => handleReject()}
                        disabled={isProcessing}
                    >
                        <CallEndIcon />
                        <span>Reject</span>
                    </button>

                    <button
                        className="call-button accept"
                        onClick={handleAccept}
                        disabled={isProcessing}
                    >
                        <PhoneIcon />
                        <span>{isProcessing ? 'Accepting...' : 'Accept'}</span>
                    </button>
                </div>

                {/* WebRTC Note */}
                <p className="call-note">
                    {currentCall.metadata?.session_sdp
                        ? '✓ SDP offer received - WebRTC ready'
                        : '⏳ Waiting for SDP...'}
                </p>
            </div>
        </div>
    );
}

export default IncomingCallModal;

